import { cn } from '@amore/ui/cn'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
} from '@amore/ui/components/combobox'
import { Field } from '@amore/ui/components/field'
import { UserRound } from 'lucide-react'
import { type ReactNode, type Ref, useState } from 'react'
import { useWedding } from '@/mock/store'
import type { CeremonyRole } from '@/mock/types'
import { ceremonyPeople, personKey } from './step-model'

export const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'
export const editorSections =
  'divide-y divide-border space-y-0 [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1'

export function CeremonyFieldLabel({ children }: { children: ReactNode }) {
  return <span className="text-sm font-normal text-muted-foreground">{children}</span>
}

export function CeremonyTitleField({
  label,
  value,
  onChange,
  error,
  readOnly,
  autoFocus,
  ref,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  readOnly: boolean
  autoFocus?: boolean
  ref?: Ref<HTMLTextAreaElement>
}) {
  return (
    <div className="space-y-1">
      <textarea
        ref={ref}
        rows={1}
        maxLength={150}
        value={value}
        readOnly={readOnly}
        data-autofocus={autoFocus || undefined}
        aria-label={label}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? 'ceremony-title-error' : undefined}
        placeholder={label}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'field-sizing-content -mx-2 w-[calc(100%+1rem)] resize-none rounded-lg bg-transparent px-2 py-1 font-heading text-[1.75rem] leading-tight tracking-tight outline-none transition-colors',
          !readOnly && 'hover:bg-muted/60 focus:bg-muted/60',
        )}
      />
      {error ? (
        <p id="ceremony-title-error" role="alert" className="text-sm text-danger-foreground">
          {error}
        </p>
      ) : null}
    </div>
  )
}

export function CeremonyPersonField({
  value,
  onChange,
  disabled,
}: {
  value: CeremonyRole['person']
  onChange: (person: CeremonyRole['person']) => void
  disabled: boolean
}) {
  const data = useWedding()
  const [query, setQuery] = useState('')
  const options = ceremonyPeople(data)
  const key = value ? personKey(value) : 'none'
  const selected = options.find((option) => personKey(option.person) === key)
  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLocaleLowerCase('cs')
  const needle = normalize(query)
  const matches = options.filter((option) =>
    normalize(`${option.name} ${option.category}`).includes(needle),
  )
  return (
    <Field
      controlId="ceremony-assigned-person"
      label={
        <span className="flex items-center gap-2 text-sm font-normal text-muted-foreground">
          <UserRound className="size-4" aria-hidden="true" />
          Má na starosti
        </span>
      }
    >
      <Combobox
        value={key}
        displayValue={selected?.name ?? (value ? 'Dříve přiřazený člověk' : '')}
        query={query}
        onQueryChange={setQuery}
        disabled={disabled}
        onValueChange={(next) => {
          if (next === 'none') onChange(null)
          else {
            const option = options.find((option) => personKey(option.person) === next)
            if (option) onChange(option.person)
          }
        }}
      >
        <ComboboxInput variant="soft" className={softControl} placeholder="Zatím nepřiřazeno" />
        <ComboboxContent>
          <ComboboxList>
            {!query ? <ComboboxOption value="none">Zatím nepřiřazeno</ComboboxOption> : null}
            {matches.map((option) => (
              <ComboboxOption key={personKey(option.person)} value={personKey(option.person)}>
                <span className="block">{option.name}</span>
                <span className="block text-xs text-muted-foreground">{option.category}</span>
              </ComboboxOption>
            ))}
            {!matches.length ? <ComboboxEmpty>Nikdo neodpovídá hledání.</ComboboxEmpty> : null}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </Field>
  )
}
